import { Link } from "react-router-dom";
import { defineListView } from "@/base/list/defineListView";
import {
  MODIFIED_OR_CREATED_SORT,
  sortedDateColumn,
  sortedDateField,
} from "@/base/list/modifiedOrCreatedSort";
import type { AuthedRequester } from "@/lib/brokerAuth";
import { listAccounts, type AccountSummary } from "@/lib/brokerAccounts";
import { ENTITY_TYPE_LABELS } from "@/lib/entityTypes";
import { primaryPhone } from "@/lib/phone";
import { buttonClass } from "@/components/buttonStyles";

// The brokerage's own accounts as a BaseListView definition (BRK-S211). The
// list shows summaries only — never SSN or EIN — with search, an
// include-inactive toggle, sort by Last modified (the default) or Date created,
// and paging; each row links to the record. "Add account" is gated on
// account:edit (the server enforces the same).
//
// Mounted twice (BRK-S138): the Surety platform lists the SAME own-scoped
// accounts, so only where a row goes differs (`basePath`).

// The list's one call to action, on the one button style (BRK-S264).
const ACTION = buttonClass();

export function accountsListView({
  basePath,
  request,
  canEdit,
}: {
  basePath: string;
  request: AuthedRequester;
  canEdit: boolean;
}) {
  return defineListView<AccountSummary>({
    endpoint: "/api/broker/accounts",
    noun: "accounts",
    title: "Accounts",
    context: "The insured accounts your brokerage works.",
    primaryAction: canEdit ? (
      <Link to="/accounts/new" className={ACTION}>
        Add account
      </Link>
    ) : undefined,
    columns: [
      {
        id: "account",
        header: "Account",
        kind: "title",
        cell: (a) => a.legalName,
        subtitle: (a) =>
          a.dbaName && a.dbaName !== a.legalName ? a.dbaName : undefined,
      },
      {
        id: "number",
        header: "Account number",
        kind: "mono",
        cell: (a) => a.accountNumber,
      },
      {
        id: "entityType",
        header: "Entity type",
        cell: (a) => (a.entityType ? ENTITY_TYPE_LABELS[a.entityType] : "—"),
      },
      {
        id: "phone",
        header: "Phone",
        cell: (a) => primaryPhone(a.phones)?.number ?? "—",
      },
      { id: "email", header: "Email", cell: (a) => a.emailAddress || "—" },
      sortedDateColumn<AccountSummary>(),
    ],
    rowKey: (a) => a.id,
    rowHref: (a) => `${basePath}/${a.id}`,
    rowLabel: (a) => `${a.legalName}, ${a.accountNumber}`,
    search: {
      urlKey: "q",
      apiParam: "search",
      label: "Search accounts",
      placeholder: "Search by name, DBA, or account number",
    },
    toggles: [
      {
        urlKey: "inactive",
        apiParam: "includeInactive",
        label: "Include inactive",
      },
    ],
    sort: MODIFIED_OR_CREATED_SORT,
    pageSize: 20,
    empty: {
      title: "No accounts found.",
      body: "The accounts your brokerage adds appear here.",
      action: canEdit ? (
        <Link to="/accounts/new" className={ACTION}>
          Add one
        </Link>
      ) : undefined,
    },
    load: ({ search, toggles, sort, limit, offset }) =>
      listAccounts(request, {
        search,
        includeInactive: toggles.includeInactive ?? false,
        field: sortedDateField(sort),
        dir: sort?.dir,
        limit,
        offset,
      }),
  });
}
